import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { DatePicker } from '@mui/lab';

import {
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import DialogModal from '../../../../../../../components/DialogModal';

import { DateFormat } from '../../../../../../../utils/date';

import texts from './texts.json';
import {
  StyledAlert,
  AlertContainer,
  StyledDialogTitle,
} from './styles';

const LaunchDateAlert = ({ launchDate, onLaunchDateChange }) => {
  const [isLaunchDateModalOpen, setIsLaunchDateModalOpen] = useState(false);
  const [newLaunchDate, setNewLaunchDate] = useState(
    launchDate
      ? moment(launchDate).format(DateFormat.DEFAULT_DATE_FORMAT)
      : moment().format(DateFormat.DEFAULT_DATE_FORMAT),
  );

  const alertContent = useMemo(() => {
    if (!launchDate) {
      return {
        severity: 'info',
        message: texts.launchAlert.noLaunchDate,
      };
    }

    const now = moment().startOf('day');
    const launchMoment = moment(launchDate).startOf('day');
    const daysUntilLaunch = launchMoment.diff(now, 'days');

    if (daysUntilLaunch > 0) {
      return {
        severity: 'success',
        message: format(texts.launchAlert.launchingIn, { days: daysUntilLaunch }),
      };
    }

    if (daysUntilLaunch === 0) {
      return {
        severity: 'success',
        message: texts.launchAlert.launchingToday,
      };
    }

    // Already launched
    return {
      severity: 'info',
      message: format(texts.launchAlert.launched, { date: launchMoment.format(DateFormat.CONTRACT_DATE_FORMAT) }),
    };
  }, [launchDate]);

  const handleEditClick = () => {
    setIsLaunchDateModalOpen(true);
  };

  const handleSave = () => {
    const formattedLaunchDate = moment(newLaunchDate).startOf('day').toDate();
    onLaunchDateChange(formattedLaunchDate);
    setIsLaunchDateModalOpen(false);
  };

  const handleCancel = () => {
    setNewLaunchDate(launchDate ? moment(launchDate).format(DateFormat.DEFAULT_DATE_FORMAT) : '');
    setIsLaunchDateModalOpen(false);
  };

  const renderEditAction = () => (
    <IconButton
      onClick={handleEditClick}
      size="small"
      sx={{
        color: 'white',
        backgroundColor: 'transparent',
        marginLeft: 1,
      }}
    >
      <EditIcon fontSize="small" />
    </IconButton>
  );

  return (
    <>
      <AlertContainer>
        <StyledAlert
          severity={alertContent.severity}
          variant="filled"
          action={renderEditAction()}
        >
          {alertContent.message}
        </StyledAlert>
      </AlertContainer>

      <DialogModal
        open={isLaunchDateModalOpen}
        onClose={handleCancel}
      >
        <StyledDialogTitle>{texts.modalTitle}</StyledDialogTitle>
        <DialogContent>
          <DatePicker
            label={texts.newStartDate}
            value={newLaunchDate}
            onChange={setNewLaunchDate}
            minDate={moment().local()}
            renderInput={(params) => (
              <TextField {...params} fullWidth />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} variant="contained" color="primary">
            {texts.save}
          </Button>
        </DialogActions>
      </DialogModal>

    </>
  );
};

LaunchDateAlert.propTypes = {
  launchDate: PropTypes.instanceOf(Date),
  onLaunchDateChange: PropTypes.func.isRequired,
};

LaunchDateAlert.defaultProps = {
  launchDate: null,
};

export default LaunchDateAlert;
